import React, { useState, useEffect, useMemo } from 'react';
import { ReactComponent as AIIcon } from '../../icons/AIIcon.svg';
import GenAIResultView from './GenAIResultView';
import { useUpdateBusinessDetails } from '../../hooks/businesshooks';
import { useCompleteSignIn } from '../../hooks/authhooks';
type GetAIViewProps = {
    isOpen: boolean;
    prompt: string;
    durationInSeconds?: number; // Optional duration for the entire sequence
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setDreamModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const GenAIView: React.FC<GetAIViewProps> = (props) => {
  const { isOpen, prompt } = props;
    const { user } = useCompleteSignIn();
    const username = user?.username || ''
    const [completed, setCompleted] = useState(false);
    const [currentAiInsight, setCurrentAiInsight] = useState("");
    const { generateBusinessInsight } = useUpdateBusinessDetails(username);

    // Reset states when prompt changes
    useEffect(() => {
      setCompleted(false);
      setCurrentAiInsight("");
    }, [prompt]);

    // Fetch insight when prompt changes
    useEffect(() => {
      const fetchInsight = async () => {
        const insight = await generateBusinessInsight(username, prompt);
        setCurrentAiInsight(insight);
      };
      if (isOpen && !completed) {
        fetchInsight();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prompt, completed, isOpen]);

    return (
      (!completed) ? 
        <GenAILoadingView {...props} setCompleted={setCompleted} /> : 
        <GenAIResultView {...props} aiInsight={currentAiInsight} />
    )
}

type GenAILoadingViewProps = {
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    durationInSeconds?: number; // Optional duration for the entire sequence
    setCompleted: React.Dispatch<React.SetStateAction<boolean>>;
}
  
const GenAILoadingView: React.FC<GenAILoadingViewProps> = (props) => {
    const { isOpen, durationInSeconds = 40, setCompleted } = props;
    const [currentStep, setCurrentStep] = useState(0);
    const [progress, setProgress] = useState(0);
    const [shouldComplete, setShouldComplete] = useState(false);
  
    const steps = useMemo(() => [
      { title: "Reviewing your submissions", targetProgress: 10, Icon: <AIIcon className="w-10 h-10 mx-auto text-primaryDark-400" /> },
      { title: "Creating your profile", targetProgress: 40, Icon: <AIIcon className="w-10 h-10 mx-auto text-primaryDark-200 scale-y-[-1]" /> },
      { title: "Determining your business stage", targetProgress: 80, Icon: <AIIcon className="w-10 h-10 mx-auto text-primaryDark-300" /> },
      { title: "Generating your checklist", targetProgress: 100, Icon: <AIIcon className="w-10 h-10 mx-auto text-primaryDark-200 scale-y-[-1]" /> }
    ], []);
  
    useEffect(() => {
      if (!isOpen) {
        setCurrentStep(0);
        setProgress(0);
        setShouldComplete(false);
        return;
      }
  
      const stepDuration = (durationInSeconds * 1000) / steps.length;
      const progressInterval = 50; // Update every 50ms for smooth animation
      
      const timer = setInterval(() => {
        setProgress(prev => {
          const currentTarget = steps[currentStep]?.targetProgress || 100;
          const nextProgress = Math.min(prev + (progressInterval / stepDuration) * (currentTarget - (steps[currentStep - 1]?.targetProgress || 0)), currentTarget);
          
          if (nextProgress >= currentTarget) {
            if (currentStep < steps.length - 1) {
              setCurrentStep(c => c + 1);
            } else if (currentStep === steps.length - 1) {
              setShouldComplete(true);
            } else {
              clearInterval(timer);
            }
          }
          
          return nextProgress;
        });
      }, progressInterval);
  
      return () => clearInterval(timer);
    }, [isOpen, steps, currentStep, durationInSeconds]);

    useEffect(() => {
      if (shouldComplete) {
        setCompleted(true);
      }
    }, [shouldComplete, setCompleted]);
  
    return (
      <div className="flex flex-col items-center justify-center p-8 min-h-[300px]">
        <div className="w-full max-w-md">
            {steps[currentStep]?.Icon}
          <div className="flex items-center justify-center my-6">
            <span className="text-primaryDark-100 text-[24px] font-raleway font-bold">
              {steps[currentStep]?.title}
            </span>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div 
              className="bg-[#5DB6CE] h-2.5 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          
          <div className="text-right mt-2 text-[16px] font-raleway font-semibold text-black">
            {Math.round(progress)}%
          </div>
        </div>
      </div>
    );
  };
  
  export default GenAIView;